import React, { useEffect } from 'react'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'

import { calculateGuaranteeDate, calculateWarrantyDate } from 'src/Libs/installation-statement/utils/warranty'
import Information from './Information'
import Images from './Images'
import Documents from './Documents'
import { useParams } from 'react-router-dom'
import { useSystemDetailsTabs } from 'src/Libs/service-dashboard/hooks/use-system-details-tabs'
import SystemDecommissionedBanner from '../decomission-banner/SystemDecommissionedBanner'
import { InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useFiles } from 'src/Libs/installation-statement/hooks'
import { UseQueryResult } from '@tanstack/react-query'
import { useWarrantyInfo } from 'src/Libs/installation-details/hooks'

interface Props {
  currentStorageInstallationStatement?: InstallationStatementModel
  refetchCurrentStorageInstallationStatement: () => Promise<UseQueryResult>
}

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}))

/**
 * Wrapper for Installation Statement page components
 */
const InstallationStatements: React.FC<Props> = ({ currentStorageInstallationStatement, refetchCurrentStorageInstallationStatement }) => {
  const { classes } = useStyles()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { setActiveTab } = useSystemDetailsTabs(installationId, storageId)

  const { storage, storageDeviceLink } = useActiveInstallationContext()
  const installationDate = storage && new Date(storage.timestampInstallDate)
  const warrantyDate = calculateWarrantyDate(installationDate, currentStorageInstallationStatement)
  const guaranteeDate = calculateGuaranteeDate(installationDate)
  const deviceLinkId = currentStorageInstallationStatement?.deviceLinkId ?? 0
  const installationIdToUse = installationId ? Number.parseInt(installationId) : undefined
  const { warrantyInfo } = useWarrantyInfo(installationId, deviceLinkId, currentStorageInstallationStatement?.id)

  const [installationStatementFiles, filesLoading, refetchFiles] = useFiles({
    deviceLinkId,
    installationId: installationIdToUse,
    filter: 'mimeType=application*',
  })

  useEffect(() => {
    setActiveTab(3)
  }, []) //eslint-disable-line

  return (
    <Box className={classes.mainContainer}>
      <BannerQueue location={BannerLocations.SystemDetails} />
      <SystemDecommissionedBanner storage={storageDeviceLink} installationId={installationId!} />
      <Information
        installationStatement={currentStorageInstallationStatement}
        installationDate={installationDate}
        warrantyDate={warrantyDate}
        guaranteeDate={guaranteeDate}
        files={installationStatementFiles}
        installationId={installationId}
        deviceLinkId={storageDeviceLink?.id}
        refetchFiles={refetchFiles}
        refetchInstallationStatement={refetchCurrentStorageInstallationStatement}
      />
      <Documents
        refetchCurrentStorageInstallationStatement={refetchCurrentStorageInstallationStatement}
        currentInstallationStatement={currentStorageInstallationStatement}
        installationId={installationId}
        files={installationStatementFiles}
        filesLoading={filesLoading}
        refetchFiles={refetchFiles}
        warrantyInfo={warrantyInfo}
      />
      <Images currentInstallationStatement={currentStorageInstallationStatement} installationId={installationId} />
    </Box>
  )
}

export default InstallationStatements
